/**
 * @file Contains the top-level rendering functions for each main application view
 * (Tasks, Patients, Projects, Assets, Settings). These functions delegate
 * the actual rendering of tables, Kanban boards, etc., to other specialized modules.
 */

// --- Imports ---

// State management
import {
    state,
} from '../state/state.js';
// Navigation
import { navigate } from '../routing/navigationController.js';
// Rendering modules
import { isBoardViewKey } from '../../shared/board/boardRegistry.js';
import { renderParentBoardView } from '../../shared/board/boardViewDispatch.js';
import { getParentBoardViewAdapters } from './parentBoardAdapterRegistry.js';
import { renderSettingsView } from '../../domains/settings/settingsController.js';
import { renderDrugsView } from '../../domains/drugs/renderDrugs.js';
import { renderRecallsView } from '../../domains/recalls/recallsView.js';
import { renderControlsSandboxView } from '../../domains/controlsSandbox/controlsSandboxView.js';
import { renderCalendarView } from '../../domains/calendar/calendarView.js';
import { renderOrderingView } from '../../domains/ordering/orderingView.js';
import { renderComplianceView } from '../../domains/compliance/complianceView.js';

function renderBoardView(viewKey) {
    const adapters = getParentBoardViewAdapters(viewKey);
    if (!adapters) {
        console.error(`renderBoardView: No parent-board adapter registered for "${viewKey}".`);
        return false;
    }

    return renderParentBoardView(viewKey, adapters);
}

// --- Core View Rendering ---

/**
 * Determines which main view rendering function to call based on `state.currentView`.
 * This acts as the central dispatcher for view updates when not in detail view.
 */
export function renderCurrentBoard() {
    if (isBoardViewKey(state.currentView)) {
        renderBoardView(state.currentView);
        return;
    }

    switch(state.currentView) {
        case 'drugs':
            renderDrugsView();
            break;
        case 'recalls':
            renderRecallsView();
            break;
        case 'calendar':
            renderCalendarView();
            break;
        case 'ordering':
            renderOrderingView();
            break;
        case 'compliance':
            renderComplianceView();
            break;
        case 'settings':
            renderSettingsView();
            break;
        case 'sandbox':
            renderControlsSandboxView();
            break;
        default:
            console.error(`renderCurrentBoard: Unknown view name "${state.currentView}". Navigating to tasks.`);
            navigate('tasks'); // Fallback to tasks view
    }
}

/**
 * Renders the content for the 'My Tasks' view, choosing between Kanban or Table layout
 * based on `state.viewSettings`.
 */
export function renderTasksView() {
    renderBoardView('tasks');
}

/**
 * Renders the content for the 'Patients' view (Kanban or Table).
 */
export function renderPatientBoard() {
    renderBoardView('patients');
}

/**
 * Renders the content for the 'Projects' view (Kanban or Table).
 */
export function renderProjectBoard() {
    renderBoardView('projects');
}

/**
 * Renders the content for the 'Assets' view (Kanban or Table).
 */
export function renderAssetBoard() {
    renderBoardView('assets');
}

/**
 * Renders the hidden Labs board shell when enabled by registry metadata.
 */
export function renderLabsBoard() {
    renderBoardView('labs');
}

/**
 * Renders the hidden Maintenance board shell when enabled by registry metadata.
 */
export function renderMaintenanceBoard() {
    renderBoardView('maintenance');
}
